// Single source of truth for the head tags on the public (unauthenticated) pages.
//
// Read from two places that must never disagree:
//   - the Cloudflare Pages Functions in /functions, which inject these into the
//     served HTML so crawlers that don't run JavaScript can see them;
//   - usePageSeo(), which re-applies them during client-side navigation.
//
// Google's guidance on rel=canonical is match-or-omit: a JS-injected canonical
// that *conflicts* with one in the raw HTML is worse than no canonical at all,
// because the raw HTML is read before the page is rendered. Keeping one table
// means the two paths cannot drift into that state.
// https://developers.google.com/search/docs/crawling-indexing/javascript/javascript-seo-basics

export const CANONICAL_HOST = 'app.qweebi.com';
export const CANONICAL_ORIGIN = `https://${CANONICAL_HOST}`;

export const DEFAULT_TITLE = 'Qweebi - Dashboard';

export type PageSeo = {
	title: string;
	description: string;
	canonicalPath: string;
};

const LOGIN_SEO: PageSeo = {
	title: 'Log in to Qweebi | Teachers & Students',
	description:
		'Log in to Qweebi. Teachers can open the dashboard to assign projects, track progress, and review student work. Students will be directed to the Qweebi Makerspace.',
	canonicalPath: '/login',
};

export const PAGE_SEO = {
	// `/` sends a signed-out visitor to /dashboard and on to /login, but that hop
	// is a Vue Router redirect — a crawler that doesn't run JS never takes it and
	// would otherwise index `/` as an empty, default-titled page. Give it login's
	// tags and canonicalise it onto /login.
	'/': LOGIN_SEO,
	'/login': LOGIN_SEO,
	'/login/teacher': {
		title: 'Log In | Qweebi Teacher Dashboard',
		description:
			'Log in to your Qweebi teacher dashboard to assign STEM projects, track student progress, and review show-and-tell submissions.',
		canonicalPath: '/login/teacher',
	},
	'/sign-up': {
		title: 'Teacher Sign Up — Free | Qweebi Online Makerspace',
		description:
			'Create your free Qweebi teacher account and run 2 full STEM projects with unlimited classes and students — zero supplies, works on any Chromebook.',
		canonicalPath: '/sign-up',
	},
} satisfies Record<string, PageSeo>;
